import PageLoader from '@components/PageLoader'
import { pluralize } from '@/utils/plural'
import { useAdminIdeas } from '@hooks/useAdminIdeas'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdminHeading } from '../AdminLayout.styles'
import { useAdminFilter } from '../AdminFilterContext'
import ListStats from '../ListStats'
import { formatDate } from '../adminFormat'
import {
	DeletedTag,
	EmptyState,
	ErrorState,
	Muted,
	PageButton,
	PageStatus,
	Pagination,
	ResultCount,
	Row,
	ScopeTag,
	SearchInput,
	SubText,
	Table,
	TableWrap,
	Td,
	Th,
	Toolbar,
} from '../adminList.styles'

const PAGE_SIZE = 25

/**
 * Admin Ideas list: every idea across the product — trip-attached and personal —
 * searchable by name, server-paginated, and scoped to the global creation-date
 * range. Rows link to the read-only idea detail. Soft-deleted ideas are flagged.
 */
const IdeasPage: React.FC = () => {
	const navigate = useNavigate()
	const { from, to } = useAdminFilter()
	const [searchInput, setSearchInput] = useState('')
	const [debouncedQ, setDebouncedQ] = useState('')
	const [page, setPage] = useState(1)

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedQ(searchInput.trim()), 300)
		return () => clearTimeout(timer)
	}, [searchInput])

	useEffect(() => {
		setPage(1)
	}, [debouncedQ, from, to])

	const { data, isLoading, isError } = useAdminIdeas({ page, pageSize: PAGE_SIZE, q: debouncedQ })

	return (
		<>
			<AdminHeading>Ideas</AdminHeading>

			<Toolbar>
				<SearchInput
					type="search"
					aria-label="Search ideas by name"
					placeholder="Search by idea name…"
					value={searchInput}
					onChange={e => setSearchInput(e.target.value)}
				/>
				{!isLoading && !isError && data && (
					<ResultCount>
						{data.total.toLocaleString()} {pluralize(data.total, 'idea')}
					</ResultCount>
				)}
			</Toolbar>

			{isLoading ? (
				<PageLoader label="Loading ideas…" />
			) : isError || !data ? (
				<ErrorState>Could not load ideas. Please try again.</ErrorState>
			) : (
				<>
					<ListStats noun="ideas" total={data.totalAllTime} newInRange={data.total} />

					<TableWrap>
						<Table>
							<thead>
								<tr>
									<Th>Idea</Th>
									<Th>Scope</Th>
									<Th>Trip</Th>
									<Th>Created</Th>
								</tr>
							</thead>
							<tbody>
								{data.ideas.length === 0 ? (
									<tr>
										<Td colSpan={4}>
											<EmptyState>No ideas match this filter.</EmptyState>
										</Td>
									</tr>
								) : (
									data.ideas.map(idea => (
										<Row
											key={idea.id}
											$deleted={idea.isDeleted}
											onClick={() => navigate(`/admin/ideas/${idea.id}`)}
										>
											<Td>
												{idea.name}
												{idea.isDeleted && <DeletedTag>Deleted</DeletedTag>}
												<SubText>{idea.category}</SubText>
											</Td>
											<Td>
												<ScopeTag $personal={idea.scope === 'personal'}>
													{idea.scope === 'personal' ? 'Personal' : 'Trip'}
												</ScopeTag>
											</Td>
											<Td>{idea.tripName ?? <Muted>—</Muted>}</Td>
											<Td>{formatDate(idea.createdAt)}</Td>
										</Row>
									))
								)}
							</tbody>
						</Table>
					</TableWrap>

					<Pagination>
						<PageStatus>
							Page {data.page} of {data.totalPages}
						</PageStatus>
						<PageButton disabled={data.page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
							← Prev
						</PageButton>
						<PageButton disabled={data.page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
							Next →
						</PageButton>
					</Pagination>
				</>
			)}
		</>
	)
}

export default IdeasPage
